import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Computer,
  Smartphone,
  Tablet,
  Refresh,
  Link as LinkIcon,
  Delete
} from '@mui/icons-material';
import { useI18n } from '../i18n/i18n';
import electronAPI from '../services/electronAPI';

const DeviceRegistration = () => {
  const { t } = useI18n();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [registrationCode, setRegistrationCode] = useState('');
  const [newDevice, setNewDevice] = useState({
    name: '',
    type: 'desktop'
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [revokeDialog, setRevokeDialog] = useState({ open: false, device: null });

  // Стан для реєстрації нового пристрою
  const [registration, setRegistration] = useState({
    code: '',
    expiresAt: null,
    isGenerating: false
  });

  const loadDevices = async () => {
    try {
      const response = await electronAPI.getDevices();
      setDevices(response || []);
    } catch (error) {
      console.error('Failed to load devices:', error);
      setMessage({
        type: 'error',
        text: t('deviceRegistration.loadFailed')
      });
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const generateRegistrationCode = async () => {
    setRegistration(prev => ({ ...prev, isGenerating: true }));
    setMessage({ type: '', text: '' });

    try {
      const result = await electronAPI.generateRegistrationCode(newDevice);
      
      setRegistration({
        code: result.registrationCode,
        expiresAt: new Date(Date.now() + result.expiresIn),
        isGenerating: false
      });

      setMessage({
        type: 'success',
        text: t('deviceRegistration.codeGenerated')
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || t('deviceRegistration.generationFailed')
      });
      setRegistration(prev => ({ ...prev, isGenerating: false }));
    }
  };

  const registerDevice = async () => {
    if (!registrationCode.trim()) {
      setMessage({
        type: 'error',
        text: t('deviceRegistration.enterCode')
      });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await electronAPI.registerDevice(registrationCode);
      
      setMessage({
        type: 'success',
        text: t('deviceRegistration.registrationSuccess', { name: result.deviceName })
      });
      
      setRegistrationCode('');
      await loadDevices();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || t('deviceRegistration.registrationFailed')
      });
    } finally {
      setLoading(false);
    }
  };

  const openRevokeDialog = (device) => {
    setRevokeDialog({ open: true, device });
  };

  const closeRevokeDialog = () => {
    setRevokeDialog({ open: false, device: null });
  };

  const revokeDevice = async () => {
    if (!revokeDialog.device) return;

    try {
      await electronAPI.revokeDevice(revokeDialog.device.id);
      setMessage({
        type: 'success',
        text: t('deviceRegistration.deviceRevoked')
      });
      await loadDevices();
      closeRevokeDialog();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || t('deviceRegistration.revokeFailed')
      });
    }
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'mobile': return <Smartphone />;
      case 'tablet': return <Tablet />;
      default: return <Computer />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'success';
      case 'offline': return 'default';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const formatTimeRemaining = (expiresAt) => {
    if (!expiresAt) return '';
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffMs = expires - now;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins <= 0) return t('deviceRegistration.expired');
    return t('deviceRegistration.expiresIn', { minutes: diffMins });
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        📱 {t('deviceRegistration.title')}
      </Typography>

      <Grid container spacing={3}>
        {/* Generate Registration Code */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('deviceRegistration.generateCode')}
              </Typography>

              <TextField
                fullWidth
                label={t('deviceRegistration.deviceName')}
                value={newDevice.name}
                onChange={(e) => setNewDevice(prev => ({ ...prev, name: e.target.value }))}
                margin="normal"
                placeholder={t('deviceRegistration.namePlaceholder')}
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>{t('deviceRegistration.deviceType')}</InputLabel>
                <Select
                  value={newDevice.type}
                  label={t('deviceRegistration.deviceType')}
                  onChange={(e) => setNewDevice(prev => ({ ...prev, type: e.target.value }))}
                >
                  <MenuItem value="desktop">
                    <Box display="flex" alignItems="center">
                      <Computer sx={{ mr: 1 }} />
                      {t('deviceRegistration.desktop')}
                    </Box>
                  </MenuItem>
                  <MenuItem value="mobile">
                    <Box display="flex" alignItems="center">
                      <Smartphone sx={{ mr: 1 }} />
                      {t('deviceRegistration.mobile')}
                    </Box>
                  </MenuItem>
                  <MenuItem value="tablet">
                    <Box display="flex" alignItems="center">
                      <Tablet sx={{ mr: 1 }} />
                      {t('deviceRegistration.tablet')}
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              <Button
                fullWidth
                variant="contained"
                onClick={generateRegistrationCode}
                disabled={!newDevice.name.trim() || registration.isGenerating}
                startIcon={registration.isGenerating ? <CircularProgress size={20} /> : <LinkIcon />}
                sx={{ mt: 2 }}
              >
                {registration.isGenerating 
                  ? t('deviceRegistration.generating') 
                  : t('deviceRegistration.generateCode')
                }
              </Button>

              {registration.code && (
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    mt: 2,
                    textAlign: 'center',
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }}
                >
                  <Typography variant="h3" fontWeight="bold" letterSpacing={2}>
                    {registration.code}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                    {formatTimeRemaining(registration.expiresAt)}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.8 }}>
                    {t('deviceRegistration.enterOnDevice')}
                  </Typography>
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Register Device */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('deviceRegistration.registerDevice')}
              </Typography>

              <TextField
                fullWidth
                label={t('deviceRegistration.registrationCode')}
                value={registrationCode}
                onChange={(e) => setRegistrationCode(e.target.value.toUpperCase())}
                margin="normal"
                placeholder="ABC-123"
                inputProps={{
                  style: { textTransform: 'uppercase' }
                }}
              />

              <Button
                fullWidth
                variant="outlined"
                onClick={registerDevice}
                disabled={!registrationCode.trim() || loading}
                startIcon={loading ? <CircularProgress size={20} /> : <LinkIcon />}
                sx={{ mt: 2 }}
              >
                {loading 
                  ? t('deviceRegistration.registering') 
                  : t('deviceRegistration.registerDevice')
                }
              </Button>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('deviceRegistration.howToRegister')}
                </Typography>
                <Typography variant="caption" display="block" color="textSecondary">
                  1. {t('deviceRegistration.step1')}
                </Typography>
                <Typography variant="caption" display="block" color="textSecondary">
                  2. {t('deviceRegistration.step2')}
                </Typography>
                <Typography variant="caption" display="block" color="textSecondary">
                  3. {t('deviceRegistration.step3')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Message Alert */}
        {message.text && (
          <Grid item xs={12}>
            <Alert 
              severity={message.type} 
              onClose={() => setMessage({ type: '', text: '' })}
            >
              {message.text}
            </Alert>
          </Grid>
        )}

        {/* Registered Devices */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  {t('deviceRegistration.registeredDevices')} ({devices.length})
                </Typography>
                <Button
                  startIcon={<Refresh />}
                  onClick={loadDevices}
                >
                  {t('common.refresh')}
                </Button>
              </Box>

              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('deviceRegistration.device')}</TableCell>
                      <TableCell>{t('deviceRegistration.status')}</TableCell>
                      <TableCell>{t('deviceRegistration.lastSeen')}</TableCell>
                      <TableCell>{t('deviceRegistration.registered')}</TableCell>
                      <TableCell>{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {devices.map((device) => (
                      <TableRow key={device.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            {getDeviceIcon(device.type)}
                            <Box ml={1}>
                              <Typography variant="body2">
                                {device.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {device.type} • {device.id?.slice(0, 8) || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(`deviceStatus.${device.status}`)}
                            color={getStatusColor(device.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {device.last_seen 
                            ? new Date(device.last_seen).toLocaleString()
                            : t('common.never')
                          }
                        </TableCell>
                        <TableCell>
                          {device.created_at 
                            ? new Date(device.created_at).toLocaleDateString()
                            : t('common.unknown')
                          }
                        </TableCell>
                        <TableCell>
                          <IconButton
                            color="error"
                            onClick={() => openRevokeDialog(device)}
                            title={t('deviceRegistration.revokeDevice')}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {devices.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="textSecondary" py={3}>
                            {t('deviceRegistration.noDevices')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Revoke Device Dialog */}
      <Dialog
        open={revokeDialog.open}
        onClose={closeRevokeDialog}
      >
        <DialogTitle>
          {t('deviceRegistration.revokeDevice')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deviceRegistration.confirmRevoke')}
            <br />
            <strong>{revokeDialog.device?.name}</strong> ({revokeDialog.device?.type})
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRevokeDialog}>
            {t('common.cancel')}
          </Button>
          <Button onClick={revokeDevice} color="error" variant="contained">
            {t('deviceRegistration.revokeDevice')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceRegistration;